import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';


const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const isFetchingProfile = useRef(false); // Prevent concurrent fetches
  const heartbeatRef = useRef(null);

  // Fetch user profile from database
  const fetchUserProfile = async (userId) => {
    // Prevent infinite loop - if already fetching, return immediately
    if (isFetchingProfile?.current) {
      console.log('[AuthContext.fetchUserProfile] Already fetching profile, skipping...');
      return null;
    }

    try {
      isFetchingProfile.current = true; // Mark as fetching
      console.log('[AuthContext.fetchUserProfile] Starting fetch for user:', userId);
      console.log('[AuthContext.fetchUserProfile] Querying user_profiles table...');
      const queryStartTime = Date.now();
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle();
      const queryEndTime = Date.now();
      console.log(`[AuthContext.fetchUserProfile] Query completed in ${queryEndTime - queryStartTime}ms`);
      console.log('[AuthContext.fetchUserProfile] Query result - data:', data ? 'EXISTS' : 'NULL');
      console.log('[AuthContext.fetchUserProfile] Query result - error:', error);

      if (error) {
        console.error('[AuthContext.fetchUserProfile] Error fetching profile:', error?.message);
        return null;
      }

      // If no profile exists, the trigger might have failed - try to create it
      if (!data) {
        console.warn('[AuthContext.fetchUserProfile] No profile found, attempting to create...');
        try {
          // Get user data from auth
          console.log('[AuthContext.fetchUserProfile] Getting user from auth...');
          const { data: { user } } = await supabase?.auth?.getUser();
          console.log('[AuthContext.fetchUserProfile] User from auth:', user ? user?.id : 'NULL');
          
          if (user) {
            // Validate role - EXPLICITLY filter out 'merchant' and only allow 'user' or 'admin'
            const rawRole = user?.user_metadata?.role || user?.raw_user_meta_data?.role || 'user';
            console.log('[AuthContext.fetchUserProfile] Raw role from metadata:', rawRole);
            let validRole = 'user'; // Default to 'user'
            
            // Only set to 'admin' if explicitly 'admin', otherwise always 'user'
            if (rawRole === 'admin') {
              validRole = 'admin';
            } else if (rawRole === 'user') {
              validRole = 'user';
            } else {
              // For any other value (including 'merchant'), default to 'user'
              validRole = 'user';
              console.log('[AuthContext.fetchUserProfile] Invalid role detected, defaulting to user');
              
              // Update user metadata to remove invalid role
              try {
                console.log('[AuthContext.fetchUserProfile] Updating user metadata to fix role...');
                await supabase?.auth?.updateUser({
                  data: {
                    ...user?.user_metadata,
                    role: validRole
                  }
                });
                console.log('[AuthContext.fetchUserProfile] User metadata updated');
              } catch (updateError) {
                console.error('[AuthContext.fetchUserProfile] Failed to update user metadata:', updateError?.message);
              }
            }
            
            console.log('[AuthContext.fetchUserProfile] Valid role determined:', validRole);
            console.log('[AuthContext.fetchUserProfile] Creating profile manually...');
            // Create profile manually
            const { data: newProfile, error: createError } = await supabase
              ?.from('user_profiles')
              ?.insert({
                id: user?.id,
                email: user?.email,
                full_name: user?.user_metadata?.full_name || user?.user_metadata?.company_id || user?.email?.split('@')?.[0],
                company_id: user?.user_metadata?.company_id || null,
                role: validRole
              })
              ?.select()
              ?.maybeSingle();

            console.log('[AuthContext.fetchUserProfile] Profile creation result:', newProfile ? 'SUCCESS' : 'FAILED');
            console.log('[AuthContext.fetchUserProfile] Profile creation error:', createError);

            if (createError) {
              console.error('[AuthContext.fetchUserProfile] Failed to create profile:', createError?.message);
              return null;
            }
            
            console.log('[AuthContext.fetchUserProfile] Profile created successfully, converting to camelCase...');
            // Return the newly created profile in camelCase format
            return {
              id: newProfile?.id,
              fullName: newProfile?.full_name,
              email: newProfile?.email,
              phone: newProfile?.phone,
              role: newProfile?.role,
              companyId: newProfile?.company_id,
              profilePictureUrl: newProfile?.profile_picture_url,
              isVerified: newProfile?.is_verified,
              rating: newProfile?.rating,
              totalPurchases: newProfile?.total_purchases,
              totalSales: newProfile?.total_sales,
              walletBalance: newProfile?.wallet_balance,
              createdAt: newProfile?.created_at,
              updatedAt: newProfile?.updated_at
            };
          }
        } catch (createError) {
          console.error('[AuthContext.fetchUserProfile] Exception creating profile:', createError?.message);
          console.error('[AuthContext.fetchUserProfile] Create error stack:', createError?.stack);
          return null;
        }
        
        console.log('[AuthContext.fetchUserProfile] No user found in auth, returning null');
        return null;
      }

      console.log('[AuthContext.fetchUserProfile] Profile found, converting to camelCase...');
      // Convert snake_case to camelCase for easier use in components
      return {
        id: data?.id,
        fullName: data?.full_name,
        email: data?.email,
        phone: data?.phone,
        role: data?.role,
        companyId: data?.company_id,
        profilePictureUrl: data?.profile_picture_url,
        isVerified: data?.is_verified,
        rating: data?.rating,
        totalPurchases: data?.total_purchases,
        totalSales: data?.total_sales,
        walletBalance: data?.wallet_balance,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('[AuthContext.fetchUserProfile] CATCH - Error in fetchUserProfile:', error?.message);
      console.error('[AuthContext.fetchUserProfile] Error stack:', error?.stack);
      return null;
    } finally {
      isFetchingProfile.current = false; // Reset flag when done
    }
  };

  // Update last_seen timestamp for the current user
  const updateLastSeen = async (userId) => {
    if (!userId) return;
    try {
      await supabase?.rpc('update_last_seen');
    } catch (err) {
      // Silently ignore - non-critical
    }
  };

  // Start heartbeat interval to keep last_seen fresh
  const startHeartbeat = (userId) => {
    stopHeartbeat();
    updateLastSeen(userId); // Immediate update on login
    heartbeatRef.current = setInterval(() => {
      updateLastSeen(userId);
    }, 5 * 60 * 1000); // Every 5 minutes
  };

  const stopHeartbeat = () => {
    if (heartbeatRef?.current) {
      clearInterval(heartbeatRef?.current);
      heartbeatRef.current = null;
    }
  };

  useEffect(() => {
    // Check for Supabase session
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        
        // Fetch profile if user exists
        if (currentUser) {
          const userProfile = await fetchUserProfile(currentUser?.id);
          setProfile(userProfile);
          startHeartbeat(currentUser?.id);
        } else {
          setProfile(null);
        }
      } catch (error) {
        console.error('[AuthContext] Error checking session:', error?.message);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Listen for Supabase auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(async (event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      
      // Only fetch profile on SIGNED_IN event to prevent infinite loops
      if (event === 'SIGNED_IN' && currentUser) {
        const userProfile = await fetchUserProfile(currentUser?.id);
        setProfile(userProfile);
        startHeartbeat(currentUser?.id);
      } else if (event === 'SIGNED_OUT') {
        setProfile(null);
        stopHeartbeat();
      }
      // For other events (TOKEN_REFRESHED, etc.), don't refetch profile
    });

    return () => {
      subscription?.unsubscribe();
      stopHeartbeat();
    };
  }, []);

  const signIn = async (email, password) => {
    try {
      console.log('[AuthContext] Starting sign in...');
      
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error('[AuthContext] Sign in error:', error?.message);
        throw error;
      }
      
      console.log('[AuthContext] Sign in successful, user ID:', data?.user?.id);
      
      // Update user state immediately
      setUser(data?.user);
      
      // Fetch profile after sign in with timeout protection
      if (data?.user) {
        try {
          console.log('[AuthContext] Fetching user profile with timeout protection...');
          
          // Wrap profile fetch with timeout to prevent indefinite hanging
          const PROFILE_TIMEOUT = 6000; // 6 seconds total timeout
          const profilePromise = fetchUserProfile(data?.user?.id);
          const timeoutPromise = new Promise((resolve) => 
            setTimeout(() => {
              console.warn('[AuthContext] Profile fetch timeout - proceeding without profile');
              resolve(null);
            }, PROFILE_TIMEOUT)
          );
          
          const userProfile = await Promise.race([profilePromise, timeoutPromise]);
          
          if (userProfile) {
            console.log('[AuthContext] Profile loaded successfully:', userProfile?.id);
            setProfile(userProfile);
          } else {
            console.warn('[AuthContext] Profile is null - user can still proceed');
            setProfile(null);
          }
        } catch (profileError) {
          console.error('[AuthContext] Profile fetch failed during sign in:', profileError?.message);
          console.log('[AuthContext] Allowing sign in to complete without profile');
          setProfile(null);
          // Don't throw - allow sign in to complete even if profile fetch fails
        }
        // Start heartbeat after successful sign in
        startHeartbeat(data?.user?.id);
      }
      
      console.log('[AuthContext] Sign in process completed');
      return data;
    } catch (error) {
      console.error('[AuthContext] Sign in error:', error?.message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await supabase?.auth?.signOut();
    } catch (error) {
      console.warn('Supabase signOut error (ignored):', error?.message);
    } finally {
      setUser(null);
      setProfile(null);
    }
  };

  const value = {
    user,
    profile,
    loading,
    signIn,
    signOut,
    userRole: profile?.role // Convenience accessor
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};